import { useEffect, useRef, useState } from 'react';

import { Link, useLocation } from 'react-router-dom';
import { fetchJsonWithAuth } from '../shared/fetch-json.util';
import NoAccessEmployee from './NoAccessEmployee';
import { useUserInfo } from '../shared/UserInfo';
import { useAPIProgress } from '../shared/APIProgress';
import { useStatusMessages } from '../shared/StatusMessages';
import MessageBanner from '../shared/MessageBanner';
import Strategy from './search/StrategyCard';
import {
  Box,
  Button,
  Card,
  CardContent,
  List,
  ListItem,
  Stack,
  Tooltip,
  Typography
} from '@mui/material';
import { Search } from '@deere/fuel-react/Search.js';
import { PageContent } from '@deere/fuel-react/PageContent.js';
import StrategyWithSortandPagination from './StrategyWithSortandPagination';
import MGCFilter, { getMGCHierarchyFromCode } from '../filters/MGCFilter';
import NoRecordMessage from './NoRecordMessage';
import {
  addPropAndValue,
  getUnitGropAndUnitsList,
  getUserName,
  ignoreCaseSearch,
  isScopeAnyGlobalType,
  utf8SafeDecode
} from '../shared/UtilFunctions';
import { transformMasterScopeResponse } from '../shared/scopeUtils';
import SupplierFilter from '../filters/SupplierFilter';
import { callMGCMasterAPI } from '../shared/APIHelper';
import { getEnvVar } from '../utils/getEnvVar';
import ScopeFilter from '../filters/ScopeFilter';
import { useAdobeAnalytics } from '../utils/useAdobeAnalytics';
import {
  DEFAULT_STAKEHOLDER_TYPES,
  MAX_PHASE,
  MGC_FILTER_CODE,
  SCOPE_LEVEL_UNITORUNITGROUP,
  STRATEGY_STATUS_ACTIVE,
  STRATEGY_STATUS_DRAFT
} from '../shared/AppConstants';
import { FILTER_TOOL_TIP } from '../shared/Messages';
import { findStrategyOwnerApprovers, getNewMGCCriteria } from './StrategyUtils';
import SepPlansBanner from './SepPlansBanner';
import PhasesFilter from '../filters/PhasesFilter';
import UserFilter from '../filters/UserFilter';
import { exportSearchResultsToExcel } from './search/SearchResultsExportUtils';
import FuelUpgradeBanner from './FuelUpgradeBanner';

export default function SearchStrategy() {
  const info = useUserInfo();
  const location = useLocation();

  // Get the MGC Code if it was passed as a query parameter
  const mgcCodeQueryParam = new URLSearchParams(location.search).get('mgcCode');

  const tokenAvailable = info?.token;
  const hasAccess = info?.hasAccess;
  const hasCreateAccess = info?.hasCreateAccess;
  const loggedInUser = info?.userId;
  const defaultUser = info?.defaultUser;
  const [refreshDefaultUser, setRefreshDefaultUser] = useState(false);
  const { pushAdobeAnalytics } = useAdobeAnalytics();
  const searchRef = useRef(null);
  const [listAfterFilter, setListAfterFilter] = useState(null);
  const [masterMGCJson, setMasterMGCJson] = useState(null);
  const [masterScope, setMasterScope] = useState(null);
  const [selectedUnits, setSelectedUnits] = useState(null);
  const [selectedMGCs, setSelectedMGCs] = useState(null);
  const [selectedSuppliers, setSelectedSuppliers] = useState(null);
  const [selectedPhases, setSelectedPhases] = useState([]);
  const [strategyList, setStrategyList] = useState(null);
  const [strategies, setStrategies] = useState(null);
  const [userMap, setUserMap] = useState(null);
  const [viewResult, setViewResult] = useState(null);
  const [toolTipText, setToolTipText] = useState(null);
  const [ownerApproverList, setOwnerApproverList] = useState(null);

  const [allFilterLabelObj, setAllFilterLabelObj] = useState({});
  const [searchCriteriaForAPI, setSearchCriteriaForAPI] = useState(null);
  const { apiInProgressCount, incrementAPIInProgress, decrementAPIInProgress } =
    useAPIProgress();
  const { statusMessages, clearAllMessages, setStatusErrorMsg } =
    useStatusMessages();

  const commonProps = {
    tokenAvailable,
    setStatusErrorMsg,
    incrementAPIInProgress,
    decrementAPIInProgress,
    setParentFilter
  };

  function search(e) {
    e.preventDefault();

    callGSSAPI(true);
    storeSearchCriteriaLabels();
    window.scrollTo(0, 0);
  }

  function filterMgcCrieteria(result) {
    let outList = [];
    let category;

    if (selectedMGCs?.mgcFilterText) {
      let inputWithoutSpaces = selectedMGCs.mgcFilterText
        .replaceAll(' ', '')
        .toLowerCase();
      result.forEach((element) => {
        category = element.decorated.mgcs.toString().toLowerCase();

        if (
          category.includes(selectedMGCs.mgcFilterText) || // filter categories
          category.includes(inputWithoutSpaces) || // filter categories (input without spaces)
          category.replaceAll(' ', '').includes(selectedMGCs.mgcFilterText) || // filter categories (without spaces)
          category.replaceAll(' ', '').includes(inputWithoutSpaces)
        )
          // filter categories (input and categories without spaces)
          outList.push(element);
      });
    } else outList = result;

    return outList;
  }

  function getStakeHolderCriteria() {
    return (
      allFilterLabelObj?.stakeholderFilter || {
        stakeHolders: defaultUser,
        stakeHolderIds: [loggedInUser],
        stakeHolderTypes: DEFAULT_STAKEHOLDER_TYPES
      }
    );
  }

  function getSearchCriteria(viewResults, userCriteria) {
    let searchObj = {};

    addPropAndValue(searchObj, 'embed', 'decorate');
    if (hasCreateAccess)
      addPropAndValue(searchObj, 'status', [
        STRATEGY_STATUS_DRAFT,
        STRATEGY_STATUS_ACTIVE
      ]);
    else addPropAndValue(searchObj, 'status', [STRATEGY_STATUS_ACTIVE]);

    if (userCriteria?.stakeHolderIds?.length > 0) {
      // Default the request roles without mutating the user's filter selection.
      addPropAndValue(searchObj, 'stakeHolderCriteria', {
        ...userCriteria,
        stakeHolderTypes: userCriteria.stakeHolderTypes?.length
          ? userCriteria.stakeHolderTypes
          : DEFAULT_STAKEHOLDER_TYPES
      });
    }

    if (selectedSuppliers && selectedSuppliers.length > 0)
      addPropAndValue(searchObj, 'suppliers', selectedSuppliers.split(','));
    if (selectedPhases && selectedPhases.length > 0) {
      addPropAndValue(searchObj, 'phases', selectedPhases);
    }
    let mgcJson;

    if (mgcCodeQueryParam) {
      mgcJson = getMGCHierarchyFromCode(mgcCodeQueryParam);
      addPropAndValue(mgcJson, 'mgcFilterType', MGC_FILTER_CODE);
      setSelectedMGCs(mgcJson);
    } else mgcJson = selectedMGCs;

    if (mgcJson && Object.keys(mgcJson).length > 0) {
      // For Session
      addPropAndValue(searchObj, 'mgcs', mgcJson);

      if (mgcJson.commodities) {
        addPropAndValue(searchObj, 'commodity', mgcJson.commodities[0]);
        if (mgcJson.majors)
          addPropAndValue(searchObj, 'major', mgcJson.majors[0]);
        if (mgcJson.minors)
          addPropAndValue(searchObj, 'minor', mgcJson.minors[0]);
        if (mgcJson.segments)
          addPropAndValue(searchObj, 'segment', mgcJson.segments[0]);
      }
      if (mgcJson.mgcFilterText) {
        // Must be desc
        addPropAndValue(searchObj, 'mgcDesc', mgcJson.mgcFilterText);
      }
    }
    if (selectedUnits && selectedUnits.length > 0) {
      // Units Search
      let scopeObject = {};

      addPropAndValue(scopeObject, 'level', SCOPE_LEVEL_UNITORUNITGROUP);
      addPropAndValue(scopeObject, 'units', selectedUnits);
      addPropAndValue(searchObj, 'scope', scopeObject);

      // For Filter
      addPropAndValue(searchObj, 'units', selectedUnits);
    }

    return searchObj;
  }

  function populateSearchCriteria() {
    let sessionObj = sessionStorage.getItem('searchCriteriaLabel');
    let parsedObj = sessionObj && JSON.parse(sessionObj);

    setAllFilterLabelObj(parsedObj);

    sessionObj = sessionStorage.getItem('searchCriteria');
    parsedObj = sessionObj && JSON.parse(sessionObj);

    // Normalize legacy comma-delimited units string to an array
    if (parsedObj && typeof parsedObj.units === 'string') {
      const normalizedUnits = parsedObj.units
        .split(',')
        .map((unit) => unit.trim())
        .filter((unit) => unit.length > 0);
      parsedObj = {
        ...parsedObj,
        units: normalizedUnits
      };
      // Persist normalized format back to session storage
      sessionStorage.setItem('searchCriteria', JSON.stringify(parsedObj));
    }

    setSearchCriteriaForAPI(parsedObj);

    return parsedObj;
  }

  function setParentFilter(obj) {
    setAllFilterLabelObj((previous) => ({ ...previous, ...obj }));
  }

  function storeSearchCriteriaLabels() {
    sessionStorage.setItem(
      'searchCriteriaLabel',
      JSON.stringify(allFilterLabelObj)
    );
  }

  function storeSearchCriteria(obj) {
    sessionStorage.setItem('searchCriteria', JSON.stringify(obj));
  }

  function callScopeAPI() {
    clearAllMessages();
    incrementAPIInProgress();
    fetchJsonWithAuth(getEnvVar('VITE_MASTER_SCOPE_API_URL'))
      .then((response) => {
        setMasterScope(transformMasterScopeResponse(response.json));
      })
      .catch((error) =>
        setStatusErrorMsg(
          "Couldn't retrieve Unit List. Please try again.",
          error
        )
      )
      .finally(() => decrementAPIInProgress());
  }

  function callGSSAPI(viewResults) {
    let searchObj;
    let criteriaFromSession = true;

    if (!viewResults) {
      searchObj = populateSearchCriteria();
      if (!searchObj) {
        searchObj = getSearchCriteria(viewResults, getStakeHolderCriteria());
        if (mgcCodeQueryParam) {
          setSearchCriteriaForAPI(searchObj);
        }
        criteriaFromSession = false;
      }
      //rerender User filter component with refreshing default user
      if (
        searchObj &&
        (!searchObj.stakeHolderCriteria ||
          Object.keys(searchObj.stakeHolderCriteria).length === 0) &&
        (!searchObj.stakeholderFilter ||
          Object.keys(searchObj.stakeholderFilter).length === 0)
      )
        setRefreshDefaultUser(true);
    } else {
      searchObj = getSearchCriteria(viewResults, getStakeHolderCriteria());

      setSearchCriteriaForAPI(searchObj);
      storeSearchCriteria(searchObj);
      setViewResult(true);
      setToolTipText(null);
    }

    let searchObjTmp = { ...searchObj };
    let mgcUpdated = {
      commodity: searchObj?.commodity,
      major: searchObj?.major,
      minor: searchObj?.minor,
      segment: searchObj?.segment
    };

    addPropAndValue(
      searchObjTmp,
      'mgcs',
      searchObj.commodity && getNewMGCCriteria([mgcUpdated])
    );

    let apiUrl = getEnvVar('VITE_GSS_API_URL') + '/searchMinimal';

    clearAllMessages();
    incrementAPIInProgress();

    fetchJsonWithAuth(apiUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json;charset=utf-8'
      },
      body: JSON.stringify(searchObjTmp)
    })
      .then((response) => {
        let formList = response.json;

        // Filter results if logged-in-user has entity(s) (Initially only when there is no criteria in Session and no MGC Code passed as query param)
        if (!viewResults && !criteriaFromSession && !mgcCodeQueryParam) {
          let filteredFormList = formList.filter((item) => {
            return (
              ignoreCaseSearch(loggedInUser, item.ownerId) === 0 ||
              ignoreCaseSearch(loggedInUser, item.approverId) === 0
            );
          });

          if (filteredFormList.length > 0) formList = filteredFormList;
          else {
            formList = [];
            setRefreshDefaultUser(true);
          }
        }
        let list = filterMgcCrieteria(formList);
        list = filterPhases(list, searchObj);
        setStrategyList(list);
        searchRef.current.value = '';
        setViewResult(false);
      })
      .catch((error) =>
        setStatusErrorMsg(
          "Couldn't retrieve the Strategy info. Please try again.",
          error
        )
      )
      .finally(() => decrementAPIInProgress());
  }

  function filterPhases(result, searchObj) {
    let outList = [];
    const phases = searchObj?.phases || [];
    if (phases && phases.length > 0 && phases.length !== MAX_PHASE) {
      result.forEach((strategy) => {
        if (strategy.currentPhase && phases.includes(strategy.currentPhase))
          outList.push(strategy);
      });
    } else outList = result;
    return outList;
  }

  function clearAll() {
    setSearchCriteriaForAPI({ mgcs: {}, units: [] });
    setAllFilterLabelObj({
      stakeholderFilter: {
        stakeHolders: [],
        stakeHolderIds: [],
        stakeHolderTypes: []
      },
      phaseFilter: []
    });
    setRefreshDefaultUser(true);
    searchRef.current.value = '';
    sessionStorage.removeItem('searchCriteria');
    sessionStorage.removeItem('searchCriteriaLabel');
  }

  useEffect(() => {
    pushAdobeAnalytics('Home', info);
  }, [info]);

  useEffect(() => {
    if (refreshDefaultUser) setRefreshDefaultUser(false);
  }, [refreshDefaultUser]);

  useEffect(() => {
    if (tokenAvailable) {
      if (hasAccess) {
        findStrategyOwnerApprovers(
          setOwnerApproverList,
          incrementAPIInProgress,
          decrementAPIInProgress,
          setStatusErrorMsg
        );

        callGSSAPI(false);
        callScopeAPI();
        callMGCMasterAPI(
          setMasterMGCJson,
          incrementAPIInProgress,
          decrementAPIInProgress,
          setStatusErrorMsg,
          true
        );
      }
    }
  }, [tokenAvailable, hasAccess]);

  useEffect(() => {
    if (strategyList) {
      const list = strategyList.map((strategy) => (
        <Strategy
          key={strategy.strategyId}
          strategy={strategy}
          owner={
            userMap
              ? utf8SafeDecode(getUserName(strategy.ownerId, userMap, true))
              : strategy.ownerId
          }
          masterMGCs={masterMGCJson}
          userInfo={info}
        />
      ));
      setStrategies(list);
      setListAfterFilter(list);
    }
  }, [strategyList, userMap]);

  useEffect(() => {
    if (viewResult === false) {
      setToolTipText(FILTER_TOOL_TIP);
    }
  }, [searchCriteriaForAPI, allFilterLabelObj, selectedMGCs]);

  function filterList(e) {
    let input = e.target.value.trim().toLowerCase();
    let inputWithoutSpaces = input.replaceAll(' ', '');
    let strategy;
    let category;

    setListAfterFilter(
      input.length > 0
        ? strategies.filter((element) => {
            strategy = element.props.strategy;
            category = strategy.decorated.mgcs.toString().toLowerCase();
            return (
              category.includes(input) || // filter categories
              category.includes(inputWithoutSpaces) || // filter categories (input without spaces)
              category.replaceAll(' ', '').includes(input) || // filter categories (without spaces)
              category.replaceAll(' ', '').includes(inputWithoutSpaces) || // filter categories (input and categories without spaces)
              strategy.status.toLowerCase().includes(input) || //filter divisionKey
              strategy.strategyId.toString().includes(input) || // filter Strategy Number
              strategy.strategyId.toString().includes(inputWithoutSpaces) || // filter Strategy Number
              (strategy.decorated.scope // filter scope
                ? isScopeAnyGlobalType(strategy.decorated.scope.level)
                  ? strategy.decorated.scope.level
                  : getUnitGropAndUnitsList(strategy.decorated.scope).join()
                : ''
              )
                .toLowerCase()
                .includes(input) || // filter Unit Name
              element.props.owner.toLowerCase().includes(input)
            ); // filter owner
          })
        : strategies
    );
  }

  function refreshStrategies() {
    callGSSAPI(true);
  }

  function downloadSearchResults(e) {
    e.preventDefault();

    exportSearchResultsToExcel(
      (listAfterFilter ?? []).map((result) => result.props.strategy),
      userMap
    );
  }

  return !info ? (
    <></>
  ) : !hasAccess ? (
    <NoAccessEmployee userId={loggedInUser} />
  ) : (
    <PageContent
      sx={(theme) => ({
        display: 'flex',
        flexDirection: 'column',
        gap: theme.fuel6Tokens.JddcSpacingMedium,
        minWidth: 0,
        marginTop: `calc(-1 * ${theme.fuel6Tokens.JddcSpacingLarge})`
      })}
    >
      <FuelUpgradeBanner />
      <SepPlansBanner loggedInUser={loggedInUser} />
      <Box
        sx={(theme) => ({
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          gap: theme.fuel6Tokens.JddcSpacingMedium,
          alignItems: 'flex-start'
        })}
      >
        {/* Filter panel */}
        <Box sx={{ flexShrink: 0, width: { xs: '100%', md: 380 } }}>
          <Card>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                <fuel-icon name="filter_list" />
                <Typography variant="h3">Filter By</Typography>
              </Stack>

              <SupplierFilter
                setSelectedSuppliers={setSelectedSuppliers}
                commonProps={commonProps}
                selectedSuppliers={
                  allFilterLabelObj && allFilterLabelObj.supplierFilter
                }
              />
              <UserFilter
                commonProps={commonProps}
                selectedValues={
                  allFilterLabelObj?.stakeholderFilter || {
                    stakeHolders: [defaultUser],
                    stakeHolderIds: [loggedInUser]
                  }
                }
                masterUserList={ownerApproverList}
                refreshDefaultUser={refreshDefaultUser}
                setUserMapFn={setUserMap}
              />
              <MGCFilter
                setSelectedMGCs={setSelectedMGCs}
                masterMGCJson={masterMGCJson}
                selectedMGCList={
                  searchCriteriaForAPI && searchCriteriaForAPI.mgcs
                }
              />
              <ScopeFilter
                commonProps={commonProps}
                masterInputTreeJson={masterScope}
                setSelectedUnits={setSelectedUnits}
                selectedUnits={
                  searchCriteriaForAPI && searchCriteriaForAPI.units
                }
              />
              <PhasesFilter
                commonProps={commonProps}
                setSelectedPhases={setSelectedPhases}
                selectedValues={
                  allFilterLabelObj && allFilterLabelObj.phaseFilter
                }
              />
            </CardContent>

            <Box
              sx={(theme) => ({
                position: 'sticky',
                bottom: 0,
                p: theme.fuel6Tokens.JddcSpacingMedium,
                display: 'flex',
                gap: 1,
                bgcolor: 'background.paper'
              })}
            >
              <Button variant="secondary" onClick={clearAll} sx={{ flex: 1 }}>
                Reset Filters
              </Button>
              <Button variant="primary" onClick={search} sx={{ flex: 1 }}>
                View Results
              </Button>
            </Box>
          </Card>
        </Box>

        {/* Results panel */}
        <Card
          sx={(theme) => ({
            flexGrow: 1,
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: theme.fuel6Tokens.JddcSpacingMedium,
            p: theme.fuel6Tokens.JddcSpacingMedium
          })}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}
          >
            <Typography variant="h2">Search</Typography>
            {hasCreateAccess && (
              <Button variant="primary" component={Link} to="/createStrategy">
                Create Strategy
              </Button>
            )}
          </Box>
          <Tooltip title={toolTipText}>
            <Search
              fullWidth
              label="Filter within results (exact text match only)"
              placeholder="Filter within results (exact text match only)"
              disabled={strategies && strategies.length === 0}
              slotProps={{
                search: {
                  inputRef: searchRef,
                  onChange: filterList
                }
              }}
            />
          </Tooltip>
          <MessageBanner
            statusMessages={statusMessages}
            apiInProgressCount={apiInProgressCount}
          />
          <div role="search">
            {listAfterFilter &&
              (listAfterFilter.length === 0 ? (
                searchCriteriaForAPI ? (
                  <List>
                    <ListItem>
                      No results found. Please try a new search term or
                      expanding your filter criteria.
                    </ListItem>
                  </List>
                ) : (
                  <NoRecordMessage showAllStrategies={refreshStrategies} />
                )
              ) : (
                <StrategyWithSortandPagination
                  list={listAfterFilter}
                  onDownloadSearchResults={downloadSearchResults}
                />
              ))}
          </div>
        </Card>
      </Box>
    </PageContent>
  );
}
